import React, {
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import {
  Col, Row, Placeholder
} from '@thd-olt-component-react/core-ui';
import { Link, Accordion, AccordionBody, AccordionHeader } from '@one-thd/sui-atomic-components';
import {
  alias,
  number,
  bool,
  extend,
  params,
  QueryContext,
  shape,
  string,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { LeadGeneration, NonBuyableScheduleAMeasure } from '@thd-olt-component-react/home-services';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { RoomVo } from '@thd-olt-component-react/augmented-reality';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  FeatureBasedRecommendationsImporterVite as FeatureBasedRecommendationsImporter
} from '@thd-olt-component-react/feature-based-recommendations';
import { DynamicRecsWrapper, RecentlyViewedWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { QuestionsAndAnswersAccordionTitle } from '@thd-olt-component-react/questions-and-answers';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { RelatedSearch, PopularVariations } from '@thd-olt-component-react/related-search';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { PIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { CPPWidget } from '@thd-olt-component-react/configurator-projects';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Price } from '@thd-olt-component-react/price';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { RatingsAndReviewsAccordionTitle } from '@thd-olt-component-react/ratings-and-reviews';
import {
  QuestionsAndAnswersAccordionBody, QuestionsAndAnswersAccordionBodyLoad, QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBody, RatingsAndReviewsAccordionBodyLoad, RatingsAndReviewsAccordionBodyModel,
  SpecificationsAccordionBody, SpecificationsAccordionBodyLoad, SpecificationsAccordionBodyModel,
  ProductOverviewAccordionBody, ProductOverviewAccordionBodyLoad, ProductOverviewAccordionBodyModel
} from './dynamic-imports';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar,
  shouldDisplayIrgCollection,
  shouldDisplayIrgaccessories,
  getNonBuyableType
} from './product-utils';
import {
  HYDRATION_STANDARD_SCROLL_BUFFER,
  RECS_REQUEST_KEY
} from '../../constants';
import '../../styles/product-page.scss';
import { ProductHDDC } from './components/hddc/Product.hddc';
import ZoneCard from './components/zone-card';

export const Product = () => {
  declareContexts([QueryContext, ExperienceContext, CustomerContext]);
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const isB2BCustomer = ctx?.customer?.type === 'b2b';
  const { channel, segment = '', isServer } = ctx;
  const { itemId } = useParams();
  const storeId = useStoreId();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);
  const serviceReviewRef = useRef(null);

  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState('');

  const accordionPlaceholders = (
    <>
      <div
        id="product-section-overview"
        className="accordion-placeholder"
        ref={productOverviewRef}
      >
        <Placeholder width="100%" height="82px" color="#F5F5F5" />
      </div>
      <div
        id="product-section-key-feat"
        className="accordion-placeholder"
        ref={specificationsRef}
      >
        <Placeholder width="100%" height="82px" color="#F5F5F5" />
      </div>
      <div
        id="product-section-qa"
        className="accordion-placeholder"
        ref={questionsRef}
      >
        <Placeholder width="100%" height="82px" color="#F5F5F5" />
      </div>
      <div
        id="product-section-rr"
        className="accordion-placeholder"
        ref={ratingsRef}
      >
        <Placeholder width="100%" height="82px" color="#F5F5F5" />
      </div>
    </>
  );

  useEffect(() => {
    const el = document.querySelector('.Header3');
    setMounted(true);
    if (!el) return;
    el.classList.remove('Header3-sticky-desktop');
  }, []);

  const { data: productData } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: true
  });

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { disableScroll: true });
    }
  };

  const nonBuyableServiceType = getNonBuyableType(productData);
  const productOverviewHeading = nonBuyableServiceType === 'SFI' ? 'Product & Service Overview' : 'Product Details';

  const isHDDC = productData?.product?.details?.installation?.serviceType === 'HDDC';
  const anchorImage = productData?.product?.media?.image?.url;
  const handleAccordionChange = (accordion, canCloseAccordion = true) => {
    if (!expanded[accordion] || canCloseAccordion) {
      setExpanded((prevState) => ({ ...prevState, [accordion]: !(prevState[accordion]) }));
    }
  };

  const scrollTo = (offset, callback) => {
    const fixedOffset = offset.toFixed();
    const onScroll = () => {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  };

  const handleProductDetailsClick = (accordion) => {
    handleAccordionChange(accordion, false);
  };

  const STICKY_HEADER_HEIGHT = 115;

  const handleViewMoreDetails = () => {
    const offset = productOverviewRef.current.offsetTop - STICKY_HEADER_HEIGHT;
    scrollTo(offset, () => handleAccordionChange('#product-section-overview', false));
  };

  const handleServiceClick = (id) => {
    const ele = document.getElementById(id);
    ele.click();
  };

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound">
          <Row>
            <ErrorBoundary name="sticknav">
              <StickyNav
                itemId={itemId}
                ref={stickyNavRef}
                ratingsRef={
                  nonBuyableServiceType !== 'SAM' && nonBuyableServiceType !== 'SFI'
                    ? ratingsRef : serviceReviewRef
                }
                onRatingsClick={() => (nonBuyableServiceType !== 'SAM' && nonBuyableServiceType !== 'SFI'
                  ? handleAccordionChange('#product-section-rr', false)
                  : handleServiceClick('service-reviews'))}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => handleAccordionChange('#product-section-overview', false)}
                      role="button"
                      tabIndex={0}
                    >
                      {productOverviewHeading}
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => handleAccordionChange('#product-section-key-feat', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => handleAccordionChange('#product-section-qa', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Questions & Answers
                    </div>
                  </StickyNav.Link>
                  {nonBuyableServiceType !== 'SAM' && nonBuyableServiceType !== 'SFI' && (
                    <StickyNav.Link targetRef={ratingsRef}>
                      <div
                        className="navlink-rr"
                        onClick={() => handleAccordionChange('#product-section-rr', false)}
                        role="button"
                        tabIndex={0}
                      >
                        Customer Reviews
                      </div>
                    </StickyNav.Link>
                  )}
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>
          {!isHDDC ? (
            <>
              <Row className="u--paddingTop u--paddingBottom" name="breadcrumb-sharebelt-grp">
                <ErrorBoundary name="breadcrumb-sharebelt" useContextForProps>
                  <Col className="u__visible" sm="7" flatten>
                    <ErrorBoundary name="breadcrumbs">
                      <Breadcrumbs
                        itemId={itemId}
                        useStencilDesign
                      />
                    </ErrorBoundary>
                  </Col>
                  <Col sm="5" nopadding>
                    <ErrorBoundary name="product-info-bar">
                      <div className="product-info-bar--hide-mobile u__flex-end">
                        <ProductInfoBar itemId={itemId} />
                      </div>
                    </ErrorBoundary>
                  </Col>
                </ErrorBoundary>
              </Row>
              {mounted && shouldDisplayReferrerRecommendations() && (
                <Row>
                  <ErrorBoundary name="recs:organic_search">
                    <Col nopadding>
                      <DynamicRecsWrapper
                        schemaName="pipsem"
                        anchorId={itemId}
                        errorBoundary
                        hideATC
                        requestKey={RECS_REQUEST_KEY}
                        hydrator={{
                          className: 'grid',
                          tag: ZoneCard,
                          delay: 0,
                          scrollBuffer: 0,
                          id: 'pipsem'
                        }}
                      />
                    </Col>
                  </ErrorBoundary>
                </Row>
              )}
              <Row>
                <ErrorBoundary name="zone-a">
                  <Col nopadding={channel === 'desktop'}>
                    <Row>
                      <Col>
                        <Discontinued itemId={itemId} />
                      </Col>
                      <Col sm="7" flatten className="product--sticky">
                        <ZoneCard className="zone-card overflow u__clearfix">
                          <Row>
                            <Col sm="12" name="product-details">
                              <ErrorBoundary name="product-details-share" useContextForProps>
                                <ProductDetails itemId={itemId}>
                                  <ProductDetails.Badge itemId={itemId} />
                                  <ProductDetails.BrandCollection showCollection showBrandHyperLink />
                                  <div className="product-details__badge-title--wrapper">
                                    <ProductDetails.Title />
                                  </div>
                                  {(!isB2BCustomer) && (
                                    <div className="product-details__favorites">
                                      <Sharebelt itemId={itemId}>
                                        <ShareFavorite showCircle showFavoritesCount />
                                      </Sharebelt>
                                    </div>
                                  )}
                                  {nonBuyableServiceType !== 'SAM' && nonBuyableServiceType !== 'SFI' && (
                                    <ProductDetails.Ratings
                                      showBadge
                                      target="#product-section-rr"
                                      offsetselector="#sticky-nav"
                                      qaselector="#product-section-qa"
                                      onClick={(event) => handleProductDetailsClick(event)}
                                    />
                                  )}
                                </ProductDetails>
                              </ErrorBoundary>
                            </Col>
                          </Row>
                          <Col sm="12">
                            <ErrorBoundary name="media-gallery-desktop">
                              <MediaGalleryDesktop
                                itemId={itemId}
                                showLoader
                                highRes
                                hover
                                mainImageFetchPriority="high"
                              />
                            </ErrorBoundary>
                          </Col>
                          <Col>
                            <ErrorBoundary name="sharebelt">
                              <Sharebelt horizontal itemId={itemId}>
                                <ShareBeltShareButton email />
                                <ShareBeltPrint />
                              </Sharebelt>
                            </ErrorBoundary>
                          </Col>
                        </ZoneCard>
                        {!isServer && productData?.product?.identifiers?.roomVOEnabled && (
                          <Col className="zone-card overflow room-vo">
                            <ErrorBoundary name="RoomVo">
                              <RoomVo
                                itemId={itemId}
                              />
                            </ErrorBoundary>
                          </Col>
                        )}
                      </Col>
                      <Col sm="5" flatten className="buyarea-wrapper">
                        <div className="overflow">
                          <Row>
                            <Col className="zone-card overflow">
                              {nonBuyableServiceType === 'SAM' && (
                                <ErrorBoundary name="price">
                                  <Price
                                    channel="desktop"
                                    displayEachUom={false}
                                    itemId={itemId}
                                    large
                                    disableRangePricing
                                    skip
                                    clsRemediation={{
                                      placeholders: true,
                                      preservePlaceholders: true
                                    }}
                                  />
                                </ErrorBoundary>
                              )}
                              {nonBuyableServiceType !== 'HDQC'
                                && (
                                  <ErrorBoundary name="consumer-card-messaging">
                                    <ConsumerCardMessaging
                                      itemId={itemId}
                                      clsRemediation={{
                                        placeholders: true,
                                        preservePlaceholders: true
                                      }}
                                    />
                                  </ErrorBoundary>
                                )}
                            </Col>
                            <Col className="zone-card overflow">
                              <ErrorBoundary name="salient">
                                <SalientPoints itemId={itemId} max={3}>
                                  <SalientListItem>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <Link
                                      underline="always"
                                      onClick={handleViewMoreDetails}
                                    >
                                      View More Details
                                    </Link>
                                  </SalientListItem>
                                </SalientPoints>
                              </ErrorBoundary>
                            </Col>
                            <Col className="zone-card overflow">
                              <ErrorBoundary name="super-sku">
                                <SuperSku
                                  itemId={itemId}
                                  onChange={onChange}
                                  disableMediaAndAvailability
                                />
                              </ErrorBoundary>
                            </Col>
                            <Col className="zone-card overflow" name="non-buyable-buy-area">
                              <ErrorBoundary name="cpp-widget">
                                <CPPWidget itemId={itemId} />
                              </ErrorBoundary>
                            </Col>
                            <Col className="zone-card overflow" name="non-buyable-buy-area">
                              <ErrorBoundary name="order-sample">
                                <OrderSample itemId={itemId}>
                                  Need a closer look?
                                </OrderSample>
                              </ErrorBoundary>
                            </Col>
                            <Col className="zone-card overflow non-buyable-buy-area">
                              {nonBuyableServiceType === 'HDQC'
                                && (
                                  <ErrorBoundary>
                                    <NonBuyableQuoteCenter itemId={itemId} />
                                  </ErrorBoundary>
                                )}
                              {nonBuyableServiceType === 'SAM'
                                && (
                                  <ErrorBoundary>
                                    <div ref={serviceReviewRef}>
                                      <NonBuyableScheduleAMeasure itemId={itemId} />
                                    </div>
                                  </ErrorBoundary>
                                )}
                              {nonBuyableServiceType === 'SFI'
                                && (
                                  <ErrorBoundary>
                                    <div ref={serviceReviewRef}>
                                      <LeadGeneration itemId={itemId} />
                                    </div>
                                  </ErrorBoundary>
                                )}
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </ErrorBoundary>
              </Row>
              <ErrorBoundary name="collapsible-sections">
                <DynamicComponent
                  pageType="pip"
                  placeholder={accordionPlaceholders}
                >
                  <Hydrator
                    id="collapsible-sections-accordion"
                    className="grid"
                    placeholder={accordionPlaceholders}
                  >
                    <div className="sui-flex sui-flex-col sui-w-full">
                      <div
                        id="product-section-overview"
                        className="product-section-overview"
                        ref={productOverviewRef}
                      >
                        <Accordion
                          key="product-details-accordion"
                          expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                          onChange={() => handleAccordionChange('#product-section-overview')}
                        >
                          <AccordionHeader>
                            <h3 className="product-details-accordion-header">{productOverviewHeading}</h3>
                          </AccordionHeader>
                          <AccordionBody>
                            <DynamicComponent pageType="pip">
                              <Hydrator
                                id="product-section-product-overview"
                                className="grid"
                                waitFor={ProductOverviewAccordionBodyLoad}
                                placeholder={(
                                  <Placeholder
                                    width="100%"
                                    height="2000px"
                                    color="#F5F5F5"
                                  />
                                )}
                              >
                                <ErrorBoundary>
                                  <ProductOverviewAccordionBody
                                    itemId={itemId}
                                    showReturnMessage
                                    revJet
                                    layoutGroup={{
                                      tag: Row,
                                    }}
                                    qctx={qctx}
                                  />
                                </ErrorBoundary>
                              </Hydrator>
                            </DynamicComponent>
                          </AccordionBody>
                        </Accordion>
                      </div>
                      <div
                        id="product-section-key-feat"
                        className="product-section-key-feat"
                        ref={specificationsRef}
                      >
                        <Accordion
                          key="specifications-accordion"
                          expanded={expanded['#product-section-key-feat'] || thdSeoBotDetected}
                          onChange={() => handleAccordionChange('#product-section-key-feat')}
                        >
                          <AccordionHeader>
                            <div className="sui-flex sui-flex-col">
                              <SpecificationsAccordionTitle itemId={itemId} />
                            </div>
                          </AccordionHeader>
                          <AccordionBody>
                            <DynamicComponent pageType="pip">
                              <Hydrator
                                id="product-section-specifications"
                                className="grid"
                                waitFor={SpecificationsAccordionBodyLoad}
                                placeholder={(
                                  <Placeholder
                                    width="100%"
                                    height="700px"
                                    color="#F5F5F5"
                                  />
                                )}
                              >
                                <ErrorBoundary>
                                  <SpecificationsAccordionBody
                                    itemId={itemId}
                                    layoutGroup={{
                                      tag: Row,
                                    }}
                                    qctx={qctx}
                                  />
                                </ErrorBoundary>
                              </Hydrator>
                            </DynamicComponent>
                          </AccordionBody>
                        </Accordion>
                      </div>

                      <div
                        id="product-section-qa"
                        className="product-section-qa"
                        ref={questionsRef}
                      >
                        <Accordion
                          key="questions-answers-accordion"
                          expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                          onChange={() => handleAccordionChange('#product-section-qa')}
                        >
                          <AccordionHeader>
                            <ErrorBoundary>
                              <QuestionsAndAnswersAccordionTitle
                                noSpacing
                                itemId={itemId}
                                layoutGroup={{
                                  tag: Row,
                                }}
                                hydrator={{
                                  className: 'grid'
                                }}
                                dynamic={{
                                  pageType: 'pip'
                                }}
                              />
                            </ErrorBoundary>
                          </AccordionHeader>
                          <AccordionBody>
                            <DynamicComponent pageType="pip">
                              <Hydrator
                                id="product-section-questions-answers"
                                className="grid"
                                waitFor={QuestionsAndAnswersAccordionBodyLoad}
                                placeholder={(
                                  <Placeholder
                                    width="100%"
                                    height="700px"
                                    color="#F5F5F5"
                                  />
                                )}
                              >
                                <ErrorBoundary>
                                  <QuestionsAndAnswersAccordionBody
                                    itemId={itemId}
                                    seoPageNumber={1}
                                    layoutGroup={{
                                      tag: Row,
                                    }}
                                    qctx={qctx}
                                  />
                                </ErrorBoundary>
                              </Hydrator>
                            </DynamicComponent>
                          </AccordionBody>
                        </Accordion>
                      </div>
                      {nonBuyableServiceType !== 'SFI' && (
                        <div
                          id="product-section-rr"
                          className="product-section-rr"
                          ref={ratingsRef}
                        >
                          <Accordion
                            key="ratings-reviews-accordion"
                            expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                            onChange={() => handleAccordionChange('#product-section-rr')}
                          >
                            <AccordionHeader>
                              <ErrorBoundary>
                                <RatingsAndReviewsAccordionTitle
                                  noSpacing
                                  itemId={itemId}
                                  layoutGroup={{
                                    tag: Row,
                                  }}
                                  hydrator={{
                                    className: 'grid'
                                  }}
                                  dynamic={{
                                    pageType: 'pip'
                                  }}
                                />
                              </ErrorBoundary>
                            </AccordionHeader>
                            <AccordionBody>
                              <DynamicComponent pageType="pip">
                                <Hydrator
                                  id="product-section-ratings-reviews"
                                  className="grid"
                                  waitFor={RatingsAndReviewsAccordionBodyLoad}
                                  placeholder={(
                                    <Placeholder
                                      width="100%"
                                      height="2000px"
                                      color="#F5F5F5"
                                    />
                                  )}
                                >
                                  <RatingsAndReviewsAccordionBody
                                    itemId={itemId}
                                    qctx={qctx}
                                    prosAndCons
                                  />
                                </Hydrator>
                              </DynamicComponent>
                            </AccordionBody>
                          </Accordion>
                        </div>
                      )}
                    </div>
                  </Hydrator>
                </DynamicComponent>
              </ErrorBoundary>
              {shouldDisplayPipInStock(productData) && (
                <Row>
                  <Col nopadding>
                    <ErrorBoundary name="pipinstock">
                      <DynamicRecsWrapper
                        schemaName="pipinstock"
                        anchorId={itemId}
                        errorBoundary
                        requestKey={RECS_REQUEST_KEY}
                        hideATC
                        hydrator={{
                          className: 'grid',
                          tag: ZoneCard,
                          id: 'pipinstock',
                          scrollBuffer: 500
                        }}
                      />
                    </ErrorBoundary>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <ProductHDDC
              itemId={itemId}
              channel={channel}
              segment={segment}
              onChange={onChange}
            />
          )}

          {(!isHDDC && shouldDisplayIrgCollection(productData)) && (
            <DynamicRecsWrapper
              schemaName="brand_based_collection"
              anchorId={itemId}
              requestKey={RECS_REQUEST_KEY}
              isCategoryTab
              isIRG
              hideFavorites={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_brand_based_collection',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          {(!isHDDC && shouldDisplayIrgaccessories(productData)) && (
            <DynamicRecsWrapper
              schemaName="irgaccessories"
              anchorId={itemId}
              requestKey={RECS_REQUEST_KEY}
              isIRG
              hideFavorites={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_irgaccessories',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          {!isHDDC && (
            <>
              <Row>
                <Col>
                  <ErrorBoundary name="pip_sponsored">
                    <RetailMediaCarousel
                      breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
                      pageContext={{
                        schema: 'pip_sponsored',
                        data: { itemId },
                        isPip: true
                      }}
                      showSponsoredCarousel
                      dynamic={{
                        pageType: 'pip'
                      }}
                      hydrator={{
                        className: 'grid',
                        tag: ZoneCard,
                        delay: 1500,
                        scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                      }}
                    />
                  </ErrorBoundary>
                </Col>
              </Row>
            </>
          )}
          {!isHDDC && (
            <ErrorBoundary>
              <FeatureBasedRecommendationsImporter
                hideOnMobile
                itemId={itemId}
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                hydrator={{
                  tag: ZoneCard,
                  className: 'grid'
                }}
                dynamic={{
                  pageType: 'pip'
                }}
              />
            </ErrorBoundary>
          )}
          {!isHDDC && shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <Col>
                <ErrorBoundary name="visually-similar">
                  <DynamicRecsWrapper
                    schemaName="visuallysimilar_1_0"
                    anchorImage={anchorImage}
                    anchorId={itemId}
                    errorBoundary
                    requestKey={RECS_REQUEST_KEY}
                    hydrator={{
                      className: 'grid',
                      tag: ZoneCard,
                      id: 'visuallysimilar_1_0',
                      scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                    }}
                  />
                </ErrorBoundary>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <ErrorBoundary name="recently-viewed">
                {typeof window !== 'undefined' && (
                  <QueryProvider cacheKey="rv-pip-recs" persist>

                    <RecentlyViewedWrapper
                      schemaName="rv_gm_pip_rr"
                      errorBoundary
                      hydrator={{
                        className: 'grid',
                        tag: ZoneCard,
                        id: 'rv_gm_pip_rr',
                        delay: 2000,
                        scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                      }}
                    />
                  </QueryProvider>
                )}
              </ErrorBoundary>
            </Col>
          </Row>
          <DynamicComponent pageType="pip">
            <Hydrator
              id="product-section-seo"
              className="grid"
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              delay={2000}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 750 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              preserveCtxVal="clientStore"
            >
              <Row>
                <ErrorBoundary name="related-search">
                  <RelatedSearch
                    itemId={itemId}
                    dynamic={{
                      pageType: 'pip'
                    }}
                    hydrator={{
                      className: 'col__12-12 sui-my-4'
                    }}
                  />
                </ErrorBoundary>
              </Row>
              <Row>
                <ErrorBoundary name="popular-variations">
                  <PopularVariations
                    productId={itemId}
                    dynamic={{
                      pageType: 'pip'
                    }}
                    hydrator={{
                      className: 'col__12-12 sui-my-4'
                    }}
                  />
                </ErrorBoundary>
              </Row>
              <Row>
                <ErrorBoundary name="emt-links">
                  <EmtLinks path={pathname} />
                </ErrorBoundary>
              </Row>
            </Hydrator>
          </DynamicComponent>
        </Row>
      </ErrorBoundary>
    </>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {};

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      itemId: string(),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      details: shape({
        installation: shape({
          serviceType: string()
        })
      })
    })
  },
  {
    clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool()
        })
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      identifiers: shape({
        roomVOEnabled: bool(),
        productType: string()
      }),
    })
  },
  {
    clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
      seoDescription: string()
    })
  },
  Breadcrumbs,
  ConsumerCardMessaging,
  Discontinued,
  DynamicRecsWrapper,
  EmtLinks,
  FeatureBasedRecommendationsImporter,
  LeadGeneration,
  MediaGalleryDesktop,
  NonBuyableScheduleAMeasure,
  NonBuyableQuoteCenter,
  CPPWidget,
  OrderSample,
  PIPRedirector,
  Price,
  PopularVariations,
  ProductDetails,
  ProductInfoBar,
  ProductMetadata,
  QuestionsAndAnswersAccordionTitle,
  RatingsAndReviewsAccordionTitle,
  RecentlyViewedWrapper,
  RelatedSearch,
  SalientPoints,
  Sharebelt,
  SpecificationsAccordionTitle,
  RetailMediaCarousel,
  StickyNav,
  SuperSku,
  QuestionsAndAnswersAccordionBodyModel,
  ProductOverviewAccordionBodyModel,
  SpecificationsAccordionBodyModel,
  RatingsAndReviewsAccordionBodyModel
);
